import { useItemContext } from "@rn-primitives/accordion";
import type { PickupRequest } from "@spork/s4p-server-sdk";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

import { toastError, toastSuccess } from "@/components/AppToast";
import { PickupIcon } from "@/components/SectionItemIcon";
import {
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { formatDisplayDate } from "@/lib/date";
import { showAlert } from "@/lib/showAlert";
import { log } from "@/logger";
import { useRevokePickupRequestMutation } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

import {
    formatPickupWeekdays,
    isRecurringPickupRequest,
} from "./pickupRequestSchedule";

type PickupRequestHomeCardProps = {
    request: PickupRequest;
    childLabel?: string;
    occurrenceDate?: string;
};

function getErrorMessage(error: unknown, fallback: string): string {
    if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
    ) {
        return error.message;
    }
    return fallback;
}

function getPickupRequestSubtitle(
    request: PickupRequest,
    t: ReturnType<typeof useTranslation>["t"],
    occurrenceDate?: string,
): string {
    if (occurrenceDate) {
        return t("home.pickupRowSubtitle", {
            date: formatDisplayDate(occurrenceDate),
            picker: request.picker_name,
        });
    }

    if (isRecurringPickupRequest(request) && request.end_date) {
        const days = formatPickupWeekdays(request.days_of_week, (day) =>
            t(`ui.weekdayShort.${day}`),
        );
        return t("home.pickupRecurringRowSubtitle", {
            days,
            from: formatDisplayDate(request.pickup_date),
            to: formatDisplayDate(request.end_date),
            picker: request.picker_name,
        });
    }

    return t("home.pickupRowSubtitle", {
        date: formatDisplayDate(request.pickup_date),
        picker: request.picker_name,
    });
}

function PickupRequestTriggerContent({
    request,
    occurrenceDate,
}: {
    request: PickupRequest;
    childLabel?: string;
    occurrenceDate?: string;
}) {
    const { t } = useTranslation();
    const { isExpanded } = useItemContext();
    const subtitle = getPickupRequestSubtitle(request, t, occurrenceDate);

    return (
        <View className="min-w-0 flex-row shrink items-center gap-2">
            <PickupIcon />
            <View className="min-w-0 flex-1 shrink gap-0.5">
                <Text
                    className="min-w-0 flex-1 shrink"
                    numberOfLines={isExpanded ? undefined : 1}
                    ellipsizeMode="tail"
                >
                    {subtitle}
                </Text>

                {isRecurringPickupRequest(request) ? (
                    <Text variant="muted" numberOfLines={1}>
                        {t("pickup.recurringWeekdays", {
                            days: formatPickupWeekdays(
                                request.days_of_week,
                                (day) => t(`ui.weekday.${day}`),
                            ),
                        })}
                    </Text>
                ) : request.end_date === null ||
                  (request.end_date && request.end_date !== request.pickup_date) ? (
                    <Text variant="muted" numberOfLines={1}>
                        {t("pickup.everyDay")}
                    </Text>
                ) : null}
            </View>
        </View>
    );
}

export function PickupRequestHomeCard({
    request,
    occurrenceDate,
}: PickupRequestHomeCardProps) {
    const { t } = useTranslation();
    const [revokePickupRequest, { isLoading: isRevoking }] =
        useRevokePickupRequestMutation();
    const itemValue = String(request.id);
    const isRecurring =
        isRecurringPickupRequest(request) && Boolean(request.end_date);
    const days = formatPickupWeekdays(request.days_of_week, (day) =>
        t(`ui.weekdayShort.${day}`),
    );

    function handleRevoke() {
        showAlert(
            t("pickup.revokeConfirmTitle"),
            t("pickup.revokeConfirmMessage"),
            [
                {
                    text: t("pickup.revokeConfirmKeep"),
                    variant: "outline",
                    testID: TEST_IDS[
                        TEST_IDS.PICKUP_REQUEST_REVOKE_KEEP_BUTTON
                    ],
                },
                {
                    text: t("pickup.revoke"),
                    variant: "destructive",
                    testID: TEST_IDS[
                        TEST_IDS.PICKUP_REQUEST_REVOKE_CONFIRM_BUTTON
                    ],
                    onPress: async () => {
                        try {
                            await revokePickupRequest(request.id).unwrap();
                            toastSuccess(
                                t("pickup.revokedTitle"),
                                t("pickup.revokedBody"),
                            );
                        } catch (error) {
                            log.error("revokePickupRequest failed", error);
                            toastError(
                                t("pickup.errorTitle"),
                                getErrorMessage(
                                    error,
                                    t("pickup.revokeFailed"),
                                ),
                            );
                        }
                    },
                },
            ],
        );
    }

    const displayDate = formatDisplayDate(
        occurrenceDate ?? request.pickup_date,
    );
    const accessibilityLabel =
        !occurrenceDate && isRecurring
            ? t("pickup.expandA11yRecurringNoName", {
                  days,
                  from: formatDisplayDate(request.pickup_date),
                  to: formatDisplayDate(
                      request.end_date ?? request.pickup_date,
                  ),
                  picker: request.picker_name,
              })
            : t("pickup.expandA11yNoName", {
                  date: displayDate,
                  picker: request.picker_name,
              });

    return (
        <AccordionItem
            value={itemValue}
            testID={TEST_IDS[TEST_IDS.HOME_PICKUP_REQUEST_CARD]}
        >
            <AccordionTrigger
                className="items-center gap-3 active:bg-accent p-4"
                accessibilityLabel={accessibilityLabel}
                testID={TEST_IDS[TEST_IDS.HOME_PICKUP_REQUEST_TOGGLE]}
            >
                <PickupRequestTriggerContent
                    request={request}
                    occurrenceDate={occurrenceDate}
                />
            </AccordionTrigger>

            <AccordionContent className="border-t border-border p-0 pb-0">
                <View className="gap-3 px-3 pb-3 pt-3">
                    {request.relationship ? (
                        <Text variant="muted">
                            {t("pickup.relationship")}: {request.relationship}
                        </Text>
                    ) : null}
                    {request.note ? (
                        <Text variant="muted">{request.note}</Text>
                    ) : null}
                    <Button
                        variant="outline"
                        size="sm"
                        onPress={handleRevoke}
                        disabled={isRevoking}
                        testID={
                            TEST_IDS[TEST_IDS.HOME_PICKUP_REQUEST_REVOKE_BUTTON]
                        }
                    >
                        <Text className="text-destructive">
                            {isRevoking
                                ? t("pickup.revoking")
                                : t("pickup.revoke")}
                        </Text>
                    </Button>
                </View>
            </AccordionContent>
        </AccordionItem>
    );
}
